import digitalData from './digitalData';
import { getParams } from './params';

const DEFAULT_SOCKET_CLIENT_TIMEOUT = 60000;

export default function getSocketClient(io, $window, logger) {
  const socketClientEnabled = Boolean(digitalData($window, 'ful-dre-2-0-socket-client', false));

  const socketClientTimeout = Number(
    digitalData($window, 'ful-dre-2-0-socket-client-timeout', DEFAULT_SOCKET_CLIENT_TIMEOUT)
  );

  const params = getParams($window);
  const { orderEmail } = params;

  const xmfedre = encodeURIComponent(JSON.stringify(params));

  const socket = io({
    path: '/api/bff/fulfillment/socket.io',
    withCredentials: true,
    transports: ['websocket', 'polling'],
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 2,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    forceNew: true,
    closeOnBeforeunload: false,
    rejectUnauthorized: false,
    extraHeaders: {
      'x-mfe-dre': xmfedre,
    },
    query: {
      orderEmail,
    },
  });

  socket.on('connect', () => {
    logger.info('socket connect');
  });

  socket.on('connect_error', (error) => {
    logger.error(`socket connect error: ${error.message}`);
    logger.error(error);
  });

  socket.on('disconnect', (reason) => {
    logger.info(`socket disconnect: ${reason}`);
  });

  socket.on('listening', (orderId) => {
    logger.info(`socket listening ${orderId}`);
  });

  socket.on('deferred', (orderId) => {
    logger.info(`socket deferred ${orderId}`);
  });
  
  socket.on('ready', (orderId) => {
    logger.info(`socket ready ${orderId}`);
  });

  socket.on('error', (error) => {
    logger.error(`socket error: ${error.message}`);
    logger.error(error);
  });

  function cleanup() {
    socket.off('connect');
    socket.off('connect_error');
    socket.off('disconnect');
    socket.off('listening');
    socket.off('deferred');
    socket.off('ready');
    socket.off('error');
  }

  async function orderReady(orderId) {
    return new Promise(async (resolve, reject) => {
      let timeoutId;
      let clearHandlers;
      let done;
      let connectError;
      let disconnect;
      let ready;
      let deferred;
      let errored;

      clearHandlers = () => {
        socket.off('connect_error', connectError);
        socket.off('disconnect', disconnect);
        socket.off('ready', ready);
        socket.off('deferred', deferred);
        socket.off('error', errored);
      }

      done = (error, result) => {
        clearHandlers();
        clearTimeout(timeoutId);
        if (socket.connected) {
          socket.disconnect();
        }
        cleanup();
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      };

      connectError = (error) => done(error);
      disconnect = (reason) => done(new Error(reason));
      ready = () => done(null, true);
      deferred = () => done(null, false);
      errored = (error) => done(error);

      socket.once('connect_error', connectError);
      socket.once('disconnect', disconnect);
      socket.once('ready', ready);
      socket.once('deferred', deferred);
      socket.once('error', errored);

      if (!socketClientEnabled) {
        ready();
        return;
      }

      timeoutId = setTimeout(() => {
        logger.info('socket timeout');
        errored(new Error('Socket timeout'));
      }, socketClientTimeout);

      await socket.connect();

      socket.emit('listen', orderId);
    });
  }

  async function hello() {
    return new Promise(async (resolve, reject) => {
      let timeoutId;
      let clearHandlers;
      let done;
      let connectError;
      let disconnect;
      let hello;
      let errored;

      clearHandlers = () => {
        socket.off('connect_error', connectError);
        socket.off('disconnect', disconnect);
        socket.off('hello', hello);
        socket.off('error', errored);
      }

      done = (error, result) => {
        clearHandlers();
        clearTimeout(timeoutId);
        if (socket.connected) {
          socket.disconnect();
        }
        cleanup();
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      };

      connectError = (error) => done(error);
      disconnect = (reason) => done(new Error(reason));
      hello = (msg) => { done(null, msg); }
      errored = (error) => done(error);

      socket.once('connect_error', connectError);
      socket.once('disconnect', disconnect);
      socket.once('hello', hello);
      socket.once('error', errored);

      if (!socketClientEnabled) {
        done(null, 'not enabled');
        return;
      }

      timeoutId = setTimeout(() => {
        logger.info('socket timeout');
        errored(new Error('Socket timeout'));
      }, socketClientTimeout);

      await socket.connect();

      socket.emit('hello');
    });
  }

  return {
    cleanup,
    orderReady,
    hello,
  };
}
